/**
 * NewScheduleDialog — create a recurring schedule for an invoice or a bill.
 *
 * Pick what recurs (sales invoice or vendor bill), a source document (cloned
 * each run), a cadence + month anchor, and an optional end date. V1 generates
 * a DRAFT on each run (you review + issue/post); "Issue automatically" is a
 * disabled V1.x affordance.
 *
 * @see prototype/automations/recurring-schedules.html
 */
import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { paiseToInr } from "../../../lib/format.ts";
import { formatSourceAmount } from "./format.ts";

type Cadence = "monthly" | "quarterly" | "yearly";
type AnchorMode = "end" | "day";
type TargetType = "invoice" | "bill";

interface SourceOption {
  id: string;
  docNumber: string;
  counterparty: string;
  amount: string;
}

export interface NewScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function NewScheduleDialog({ open, onOpenChange, onCreated }: NewScheduleDialogProps) {
  const invoicesQuery = trpc.invoices.list.useQuery({});
  const billsQuery = trpc.bills.list.useQuery({});
  const create = trpc.recurringSchedules.create.useMutation();

  const [targetType, setTargetType] = useState<TargetType>("invoice");
  const [sourceDocumentId, setSourceDocumentId] = useState("");
  const [label, setLabel] = useState("");
  const [labelTouched, setLabelTouched] = useState(false);
  const [cadence, setCadence] = useState<Cadence>("monthly");
  const [anchorMode, setAnchorMode] = useState<AnchorMode>("end");
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [endsOn, setEndsOn] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loadingSources = targetType === "bill" ? billsQuery.isLoading : invoicesQuery.isLoading;

  // Normalise invoices / bills into one option shape. Recurring against
  // drafts/voids makes no sense — offer real documents only.
  const sources: SourceOption[] = useMemo(() => {
    if (targetType === "bill") {
      return (billsQuery.data?.items ?? [])
        .filter((b) => b.status !== "draft" && b.status !== "void")
        .map((b) => ({
          id: b.id,
          docNumber: b.vendorDocNumber ?? "—",
          counterparty: b.vendorName,
          amount: paiseToInr(b.totalPaise),
        }));
    }
    return (invoicesQuery.data?.items ?? [])
      .filter((i) => i.status !== "draft" && i.status !== "void")
      .map((i) => ({
        id: i.id,
        docNumber: i.docNumber ?? "—",
        counterparty: i.customerName,
        amount: formatSourceAmount(i),
      }));
  }, [targetType, billsQuery.data, invoicesQuery.data]);

  const onPickSource = (id: string) => {
    setSourceDocumentId(id);
    const src = sources.find((s) => s.id === id);
    if (src && !labelTouched) setLabel(`${src.counterparty} — recurring`);
  };

  const onPickTarget = (t: TargetType) => {
    setTargetType(t);
    setSourceDocumentId("");
    if (!labelTouched) setLabel("");
  };

  const reset = () => {
    setTargetType("invoice");
    setSourceDocumentId("");
    setLabel("");
    setLabelTouched(false);
    setCadence("monthly");
    setAnchorMode("end");
    setDayOfMonth(1);
    setEndsOn("");
    setError(null);
  };

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const canSubmit = sourceDocumentId !== "" && label.trim() !== "" && !create.isPending;

  const submit = async () => {
    setError(null);
    try {
      await create.mutateAsync({
        targetType,
        sourceDocumentId,
        label: label.trim(),
        cadence,
        monthEnd: anchorMode === "end",
        ...(anchorMode === "day" ? { dayOfMonth } : {}),
        ...(endsOn ? { endsOn } : {}),
      });
      onCreated();
      close(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create the schedule.");
    }
  };

  const docNoun = targetType === "bill" ? "bill" : "invoice";

  return (
    <DialogRoot open={open} onOpenChange={close}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>New recurring schedule</DialogTitle>
          <DialogDescription>
            Generate a document automatically on a calendar cadence. Each run clones the source{" "}
            {docNoun} into a fresh draft to review.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          <Field>
            <Label>What recurs</Label>
            <Select value={targetType} onValueChange={(v) => onPickTarget(v as TargetType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="invoice">Sales invoice</SelectItem>
                <SelectItem value="bill">Vendor bill</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <Label>Source {docNoun}</Label>
            <Select
              {...(sourceDocumentId ? { value: sourceDocumentId } : {})}
              onValueChange={onPickSource}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingSources ? "Loading…" : `Pick a ${docNoun}…`} />
              </SelectTrigger>
              <SelectContent>
                {sources.map((src) => (
                  <SelectItem key={src.id} value={src.id}>
                    {src.docNumber + " · " + src.counterparty + " · " + src.amount}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">
              Lines, currency &amp; FX carry over from this {docNoun} on every run.
            </p>
          </Field>

          <Field>
            <Label htmlFor="rs-label">Label</Label>
            <Input
              id="rs-label"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                setLabelTouched(true);
              }}
              placeholder="Taqat — monthly retainer"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label>Cadence</Label>
              <Select value={cadence} onValueChange={(v) => setCadence(v as Cadence)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <Label>When in the month</Label>
              <Select value={anchorMode} onValueChange={(v) => setAnchorMode(v as AnchorMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="end">Last day</SelectItem>
                  <SelectItem value="day">A fixed day</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {anchorMode === "day" ? (
            <Field>
              <Label htmlFor="rs-day">Day of month</Label>
              <Input
                id="rs-day"
                type="number"
                min={1}
                max={28}
                value={String(dayOfMonth)}
                onChange={(e) => setDayOfMonth(Math.min(28, Math.max(1, Number(e.target.value) || 1)))}
              />
              <p className="text-xs text-muted">
                1–28 (every month has it). For the 29th–31st use “Last day”.
              </p>
            </Field>
          ) : null}

          <Field>
            <Label htmlFor="rs-ends">Ends on (optional)</Label>
            <Input id="rs-ends" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
            <p className="text-xs text-muted">Leave blank to run until you pause or cancel it.</p>
          </Field>

          <div className="rounded-md border border-dashed border-line-2 bg-raised px-3 py-2.5 text-xs text-muted">
            On each run Flint saves a <strong className="text-ink-2">draft</strong> for review.
            Auto-issue arrives in V1.x.
          </div>

          {error ? <Alert intent="bad" title="Couldn't create schedule" description={error} /> : null}
        </div>

        <DialogFooter>
          <Button intent="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit}>
            {create.isPending ? "Creating…" : "Create schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
